import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { PhoneIcon, CalendarCheckIcon, StarIcon, ShieldCheckIcon } from 'lucide-react';
import { COMPANY } from '../../lib/site';
import { MagneticButton } from '../fx/MagneticButton';
import { ShieldEmblem } from '../ui/ShieldEmblem';
const headline = ['Protect.', 'Restore.', 'Rebuild.'];
export function Hero() {
  const {
    scrollY
  } = useScroll();
  const imgY = useTransform(scrollY, [0, 600], [0, 120]);
  const contentY = useTransform(scrollY, [0, 600], [0, -40]);
  return <section className="relative min-h-screen w-full overflow-hidden pt-28" data-id="element-249" data-mp="qtt1ve9g6i59">
      {/* background image with parallax + mask reveal */}
      <motion.div style={{
      y: imgY
    }} className="absolute inset-0 -z-[1]" data-id="element-250" data-mp="10l5q7vw1eq6j">
        <motion.img src={COMPANY.hero} alt="Luxury modern home under a bright blue sky" fetchPriority="high" decoding="async" className="h-[115%] w-full object-cover" initial={{
        scale: 1.15,
        filter: 'blur(20px)'
      }} animate={{
        scale: 1,
        filter: 'blur(0px)'
      }} transition={{
        duration: 1.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-251" data-mp="15lbcsxng9mhr" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-white/10" data-id="element-252" data-mp="3ruh33ib9swke" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-white/40" data-id="element-253" data-mp="sd936t2uoq1h" />
      </motion.div>

      <motion.div style={{
      y: contentY
    }} className="mx-auto grid max-w-7xl items-center gap-8 px-6 pb-16 pt-10 lg:grid-cols-[1.1fr_0.9fr] lg:pt-16" data-id="element-254" data-mp="nfr0vljc992g">
        {/* Foreground glass panel */}
        <motion.div initial={{
        opacity: 0,
        y: 40,
        filter: 'blur(14px)'
      }} animate={{
        opacity: 1,
        y: 0,
        filter: 'blur(0px)'
      }} transition={{
        duration: 1,
        delay: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="glass-strong relative rounded-[2.5rem] p-8 shadow-glow sm:p-10 lg:p-12" data-id="element-255" data-mp="el2v7u9uppd4">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-royal/20 bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-royal" data-id="element-256" data-mp="2qvkumm6xwpso">
            <ShieldCheckIcon className="h-4 w-4" data-id="element-257" data-mp="2humq82slag9k" />
            {COMPANY.serviceArea}
          </div>

          <h1 className="font-display text-4xl font-bold leading-[1.02] text-navy sm:text-5xl md:text-6xl" data-id="element-258" data-mp="2mvaguncwov39">
            {headline.map((word, wi) => <span key={wi} className="block overflow-hidden" data-id="element-259" data-mp="2cwcwlwmijnq2" data-mpi={wi}>
                <motion.span className="inline-block text-gradient" initial={{
              y: '110%',
              opacity: 0
            }} animate={{
              y: '0%',
              opacity: 1
            }} transition={{
              duration: 0.8,
              delay: 0.5 + wi * 0.14,
              ease: [0.22, 1, 0.36, 1]
            }} data-id="element-260" data-mp="2cwl9hoimyt19">
                  {word}
                </motion.span>
              </span>)}
            <motion.span className="mt-2 block text-2xl font-semibold text-navy sm:text-3xl" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 1,
            duration: 0.8
          }} data-id="element-261" data-mp="20kir2v7mozub">
              Everything Your Home Deserves.
            </motion.span>
          </h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.15,
          duration: 0.8
        }} className="mt-6 max-w-lg text-base leading-relaxed text-slate md:text-lg" data-id="element-262" data-mp="ty6sqdz207m9">
            Houston's premier restoration & construction team. From 24/7
            emergency water and fire recovery to luxury remodels — we restore
            homes and protect families.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.3,
          duration: 0.8
        }} className="mt-8 flex flex-wrap items-center gap-4" data-id="element-263" data-mp="7qsypb7xpnbs">
            <MagneticButton variant="primary" to="/contact" data-id="element-264" data-mp="3l4nzgebfh45w">
              <CalendarCheckIcon className="h-4 w-4" data-id="element-265" data-mp="3l21p66x5kajz" />
              Schedule Free Inspection
            </MagneticButton>
            <MagneticButton variant="emergency" href={COMPANY.phoneHref} data-id="element-266" data-mp="277uqssz51cv6">
              <PhoneIcon className="h-4 w-4" data-id="element-267" data-mp="2760cvymaal0l" />
              Emergency Call Now
            </MagneticButton>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.5,
          duration: 0.8
        }} className="mt-8 flex flex-wrap items-center gap-6" data-id="element-268" data-mp="1y6ww51alahp9">
            <a href={COMPANY.phoneHref} className="text-sm font-semibold text-navy transition-colors hover:text-fire" data-id="element-269" data-mp="1jhh7bqumcmc7">
              <span className="text-slate" data-id="element-270" data-mp="1ahu6p79prkk7">Call 24/7:</span>{' '}
              {COMPANY.phoneDisplay}
            </a>
            <div className="flex items-center gap-1.5" data-id="element-271" data-mp="15jahmxztpsnm">
              {Array.from({
              length: 5
            }).map((_, i) => <StarIcon key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" data-id="element-272" data-mp="10kg0e0hi8c5m" data-mpi={i} />)}
              <span className="ml-1 text-sm font-medium text-slate" data-id="element-273" data-mp="vvogxyuykpgr">
                5.0 Google Reviews
              </span>
            </div>
          </motion.div>
        </motion.div>

        {/* Right side emblem */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1.2,
        delay: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} className="hidden lg:block" data-id="element-274" data-mp="ly0lf6pj3dxj">
          <ShieldEmblem data-id="element-275" data-mp="14vtl804uv7qp" />
        </motion.div>
      </motion.div>

      {/* scroll indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2
    }} className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex" data-id="element-276" data-mp="1slfjmldelixn">
        <span className="text-xs font-medium uppercase tracking-widest text-slate" data-id="element-277" data-mp="3i06rpadx9rdh">
          Scroll
        </span>
        <div className="flex h-10 w-6 justify-center rounded-full border border-royal/30 pt-2" data-id="element-278" data-mp="3rs31vjta97y2">
          <motion.span className="h-2 w-1 rounded-full bg-royal" animate={{
          y: [0, 8, 0],
          opacity: [1, 0.3, 1]
        }} transition={{
          duration: 1.6,
          repeat: Infinity
        }} data-id="element-279" data-mp="3mtjdlcr7vk2j" />
        </div>
      </motion.div>
    </section>;
}